import React, {useEffect, useState} from 'react';

import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {InputChangeEvent} from 'util/Types';

/*
 * Controls includeSampleRoles state, and returns value and the checkbox element to toggle it.
 *
 * @param canHaveSampleRoles - Whether current configuration allows sample roles and should show the checkbox
 * (e.g. filling a container, not a plate).
 * Returns
 *   - includeSampleRoles: Whether table should include sample roles columns. Should be saved and passed to
 *     the structured table via the metadata.
 *   - isIncludeSampleRolesValid: Whether the value of includeSampleRoles is valid (true only if canHaveSampleRoles
 *     is true). Can be used in validating the metadata before submit.
 *   - includeSampleRolesCheckbox: Possibly null checkbox element, to be inserted into the modal component to
 *     allow toggling includeSampleRoles.
 */
export default function useIncludeSampleControlRoles({canHaveSampleRoles}: {canHaveSampleRoles: boolean}): {
  includeSampleRoles: boolean;
  isIncludeSampleRolesValid: boolean;
  includeSampleRolesCheckbox: JSX.Element | null;
} {
  const [includeSampleRoles, setIncludeSampleRoles] = useState(false);
  // Ensure includeSampleRoles is selected iff canHaveSampleRoles
  const isIncludeSampleRolesValid = !(includeSampleRoles && !canHaveSampleRoles);
  useEffect(() => {
    if (!isIncludeSampleRolesValid) {
      setIncludeSampleRoles(false);
    }
  }, [isIncludeSampleRolesValid]);

  if (!hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1)) {
    return {includeSampleRoles: false, isIncludeSampleRolesValid: true, includeSampleRolesCheckbox: null};
  }
  const checkboxLabel = hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)
    ? `Show restriction status, ${getRestrictedSampleUsersDisplayText()}, and sample owners fields`
    : `Show ${getRestrictedSampleUsersDisplayText()} and sample owners fields`;

  const includeSampleRolesCheckbox = canHaveSampleRoles ? (
    <FormGroup>
      <Label>Sample control roles</Label>
      <CheckboxInput
        label={checkboxLabel}
        onChange={(e: InputChangeEvent) => {
          setIncludeSampleRoles(e.target.checked);
        }}
        id="include-sample-roles-checkbox"
        checked={includeSampleRoles}
      />
    </FormGroup>
  ) : null;
  return {includeSampleRoles, isIncludeSampleRolesValid, includeSampleRolesCheckbox};
}
